import React, { useEffect, useState, useRef, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import API from '../../../utils/API';
import Table from '../../../components/Table/Table';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import constants from '../../../utils/constants';
import tableCheckbox from '../../../components/Inputs/tableCheckbox';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import AlertUsersAssignSiteModal from './AlertUsersAssignSiteModal';
import AlertUsersModerationModal from './AlertUsersModerationModal';
import SettingsIcon from '../../../assets/icons/alert-plus/settings.svg';
import commonFunctions from '../../../utils/commonFunctions';

function AlertUsers(props) {
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map(permission => permission.id);

  if (!permissionIds.includes(constants.permissionIds.aPlusEdit)) {
    navigate('/forbidden');
  }

  //Implemented multi-language support
  const intl = useIntl();
  const translation = (id) => intl.formatMessage({ id });

  const [alertUsers, setAlertUsers] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('email');
  const [sortByAsc, setSortByAsc] = useState(true);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const [cmsList, setCmsList] = useState([]);

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  const loadTable = async (map, resetSelection = true) => {
    // Reset map
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      // Then load user data
      const res = await API.getAlertPlusUsers();
      const alertUsersData = res.data;
      alertUsersData.forEach(user => {
        const sitesDisplay = user.sites && Array.isArray(user.sites)
          ? user.sites.map((site) => site.name).join(', ').trim()
          : ''; 

        map.data[user.id] = {
          id: user.id,
          name: user.name,
          phone: user.phone,
          roles: user.roles,
          roleNames: /*user.roles*/(commonFunctions.getRolesSort(user.roles)).map((role) => role.id ? translation(`${role.id}-ROLENAME`) : "").join(', '),
          email: user.email,
          sites: user.sites,
          sitesDisplay,
          permissions: user.permissions,
          checked: false,
        };
      });
      console.log('Alert Users:', alertUsersData);
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setAlertUsers(alertUsersData);
      setDataCount(map.ids.length);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading data:', error);
      setDataLoaded(true);
    }
  };

  const dataMap = useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
    loadTable(map);
    return map;
  }, []);

  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    startIndexForTable,
    setStartIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, alertUsers]);

  const closeModal = modalId => {
    const modal = document.getElementById(modalId);
    if (modal) {
      const closeButton = modal.querySelector('.btn-close');
      if (closeButton) {
        closeButton.click();
      }
    }
  };

  const tableColumnArray = [
    {
      Header: tableCheckbox,
      id: 'checkbox',
      accessor: 'checked',
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: 'TEACHER',
      id: 'name',
      dataIndex: 'name',
      accessor: 'name',
      isVisible: true,
    },
    {
      Header: 'PHONE_NUMBER',
      id: 'phone',
      dataIndex: 'phone',
      accessor: 'phone',
      isVisible: true,
    },
    {
      Header: 'EMAIL',
      id: 'email',
      dataIndex: 'email',
      accessor: 'email',
      isVisible: true,
    },
    {
      Header: 'SITES',
      id: 'sites',
      dataIndex: 'sites',
      accessor: 'sitesDisplay',
      isVisible: true,
    },
    {
      Header: 'ROLE',
      id: 'roleNames',
      isVisible: true,
      accessor: 'roleNames',
    },
  ];

  const tableColumns = useMemo(() => tableColumnArray, [cmsList]);

  const buttons = {
    titleButtons: (
      <>
        <div
          id="moderationOptionsBtn"
          type="button"
          className="btn-title"
          data-bs-toggle="modal"
          data-bs-target="#moderationModal"
        >
          <img src={SettingsIcon} alt="Settings" style={{'marginRight':'4px'}} />
          <FormattedMessage id="MODERATION_OPTIONS" defaultMessage="Moderation Options" />
        </div>
      </>
    ),
    footerButtons: (
      <>
        <button
          id="assignSiteBtn"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#assignSiteModal"
          disabled={Object.keys(selectedIds).length === 0}
        >
          <FormattedMessage id="ASSIGN_CMS_SITE" defaultMessage="Assign CMS/Site" />
        </button>
      </>
    )
  }

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "alertplus_users_table",
    pageType: constants.pageType.AlertPlusUsers,
    modalId: 'aplus',
    columns: tableColumns,
    data,
    setData,
    dataMap,
    success,
    setSuccess,
    error,
    setError,
    generateDataSlice,
    setDataCount,
    sortMap,
    dataCount,
    totalDataCount,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    setSortBy,
    sortByCol,
    sortByAsc,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    buttons,
  };

  const siteModalProps = {
    selectedUserIds: Object.keys(dataMap.selectedIds),
    setSuccess,
    setError,
    dataMap,
    loadTable,
  };

  const moderationModalProps = {
    setSuccess,
    setError,
  };

  return (
    <div>
      <div>
        {/* <div className="title"><FormattedMessage id="Alert Plus Users" /></div> */}
        <AlertUsersAssignSiteModal {...siteModalProps} />
        <AlertUsersModerationModal {...moderationModalProps} />
        <Table {...tableProps} />
      </div>
      {error ? (
        <div className="alert alert-danger">
          <FormattedMessage id="ERROR" />
          {error}
        </div>
      ) : null}
    </div>
  );
}

const mapStateToProps = state => ({
  user: state.user,
});

export default connect(mapStateToProps)(AlertUsers);
